<template>
  <div class="main">
    <div v-if="!initializing" class="container-card">
      <h3 class="text-center text-primary fw-bold">
        {{ $t('botones.modificarReserva') }}
      </h3>
      <div class="py-4">
        <CardInfoExperiencia />
        <CardInfoReserva @limpiar-codigo-descuento="limpiarCodigoDescuentoHandler" />
      </div>

      <CardDatosFiscales
        v-if="shouldShowDatosFiscales"
        ref="datosFiscalesRef"
        @datos-fiscales-selected="handleDatosFiscalesSelected" />

      <div v-if="!isInscripcion" class="row">
        <div class="col-12 col-md-6 pb-3">
          <template v-if="totalPendiente.value > 0">
            <FormCodigoDescuento
              :viaje-id="String(grupo.viaje.id)"
              :reservas-ids="grupo.reservas?.map(reserva => reserva.id)"
              @descuento-aplicado="codigoDescuentoHandler" />
          </template>
        </div>
        <div class="col-12 col-md-6 text-end pb-3">
          <div class="outer-card">
            <p class="fs-6 text-primary m-0 p-0 fw-bold importe">
              {{ $t('subtitulo.importePendiente') }}
            </p>
            <div class="inner-card">
              <div class="text-secondary fs-5 py-2 px-3 price">{{ totalPendiente }} {{ agenciaStore.getCurrency }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-2">
        <button
          v-if="
            grupo.viaje.estado === ESTADOS_VIAJE.PUBLICADO && grupo.viaje.continuarSinPagar && !isNextHitoRestante()
          "
          class="btn btn-secondary"
          @click="modificarSinPagar()">
          {{ $t('botones.modificar').toLocaleUpperCase() }}
        </button>

        <button class="btn btn-primary" @click="registraPagoModificar()">
          <template v-if="isInscripcion">
            {{ $t('botones.modificar').toLocaleUpperCase() }}
          </template>
          <template v-else>
            {{ $t('botones.modificarPagar').toLocaleUpperCase() }}
          </template>
        </button>
      </div>
    </div>

    <div v-if="infoPagoRedsys">
      <form name="redsysPagoForm" :action="infoPagoRedsys.url" method="POST">
        <input type="hidden" name="Ds_SignatureVersion" :value="infoPagoRedsys.signatureVersion" />
        <input type="hidden" name="Ds_MerchantParameters" :value="infoPagoRedsys.params" />
        <input type="hidden" name="Ds_Signature" :value="infoPagoRedsys.signature" />
      </form>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, nextTick, onBeforeMount } from 'vue';
  import ReservasApiService from '@/services/api/skiandnight/ReservasApiService';
  import PasarelaPagoApiService from '@/services/api/skiandnight/PasarelaPagoApiService';
  import { useRoute, useRouter } from 'vue-router';
  import FormCodigoDescuento from '@/modules/viajesGrupos/components/FormCodigoDescuento.vue';
  import { controlErrorRealizarPago } from '@/services/utils/ErrorControl';
  import CardInfoExperiencia from '@/components/card/Reserva/CardInfoExperiencia.vue';
  import CardInfoReserva from '@/components/card/Reserva/CardInfoReserva.vue';
  import CardDatosFiscales from '@/modules/viajesGrupos/components/CardDatosFiscales.vue';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import { UsuarioProximasReservasDetalle } from '@/router/RouterNames';
  import { ESTADOS_VIAJE, TIPOS_PAGO } from '@/constants/viajeConstants';
  import { HITOS_PAGO_TIPOCANTIDAD } from '@/constants/viajeConstants';
  import { ESTADOS_RESERVA } from '@/constants/reservaConstants';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { toRawDeep } from '@/helpers/reactivityUtils';
  import currency from 'currency.js';
  import { useLoadingStore } from '@/store_pinia/loadingStore';

  const route = useRoute();
  const router = useRouter();
  const loadingStore = useLoadingStore();
  const storeHabitacion = informacionHabitacion();
  const agenciaStore = agenciaStoreModule();
  const grupo = ref();
  const initializing = ref(true);
  const infoPagoRedsys = ref(null);
  const datosFiscalesRef = ref(null);
  const selectedDatosFiscalesId = ref(null);

  const reservasInDraft = computed(() =>
    storeHabitacion.getResumenReservas.filter(
      reserva => reserva.estado !== ESTADOS_RESERVA.CANCELADA && reserva.withDraft
    )
  );
  const isInscripcion = computed(
    () => grupo.value.viaje.tipoPago === TIPOS_PAGO.SIN_PAGO || grupo.value.viaje.invitacionGrupo
  );
  const totalPendiente = computed(() => {
    let totalPendienteAux = currency(0);
    storeHabitacion.getResumenReservas.map(({ precioTotal, totalPagado, descuento }) => {
      let valorPendiente = currency(precioTotal).subtract(totalPagado).subtract(descuento);
      totalPendienteAux = totalPendienteAux.add(Math.max(0, valorPendiente.value));
    });
    return totalPendienteAux;
  });

  const shouldShowDatosFiscales = computed(() => grupo.value.viaje.datosFiscalesEnabled);

  function handleDatosFiscalesSelected(id) {
    selectedDatosFiscalesId.value = id;
  }

  function moveToMisReservas() {
    router.push({
      name: UsuarioProximasReservasDetalle,
      params: { grupoId: grupo.value.id },
    });
  }

  async function registraPagoModificar() {
    try {
      loadingStore.show();

      if (datosFiscalesRef.value && !(await datosFiscalesRef.value.submit())) {
        return;
      }

      const datosFiscalesId = selectedDatosFiscalesId.value;

      const reservasId = reservasInDraft.value.map(reserva => reserva.id);
      const datosRedireccionDTO = await PasarelaPagoApiService.solicitaCobroSucesivo(
        reservasId,
        true,
        storeHabitacion.getCodigoDescuento?.codigo,
        true,
        datosFiscalesId
      );
      if (!datosRedireccionDTO.url) {
        moveToMisReservas();
      } else {
        if (datosRedireccionDTO.redsysInfo) {
          infoPagoRedsys.value = datosRedireccionDTO.redsysInfo;
          infoPagoRedsys.value.url = datosRedireccionDTO.url;
          await nextTick();
          document.redsysPagoForm.submit();
        } else {
          window.location.replace(datosRedireccionDTO.url);
        }
      }
    } catch (err) {
      console.error(err);
      controlErrorRealizarPago(err);
    } finally {
      loadingStore.hide();
    }
  }

  async function modificarSinPagar() {
    try {
      loadingStore.show();
      let reservasId = reservasInDraft.value.map(reserva => reserva.id);
      await PasarelaPagoApiService.solicitaContinuarSinPagar(reservasId);
      nextTick(() => {
        moveToMisReservas();
      });
    } catch (err) {
      console.error(err);
      controlErrorRealizarPago(err);
    } finally {
      loadingStore.hide();
    }
  }

  function codigoDescuentoHandler(informacionCodigoDescuento) {
    storeHabitacion.setCodigoDescuento(informacionCodigoDescuento);
  }

  function limpiarCodigoDescuentoHandler() {
    storeHabitacion.setCodigoDescuento(null);
  }

  function isNextHitoRestante() {
    const fechaActual = Date.now();
    const hitosFuturos = grupo.value.viaje?.hitosPago.filter(hito => new Date(hito.fecha) > fechaActual);

    if (hitosFuturos.length === 0) {
      return false;
    }

    hitosFuturos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    return hitosFuturos[0].tipoCantidad === HITOS_PAGO_TIPOCANTIDAD.RESTANTE;
  }

  async function init() {
    try {
      loadingStore.show();
      grupo.value = await ReservasApiService.getGrupo(route.params.grupoId, true);

      storeHabitacion.clearReservas();
      const grupoRaw = toRawDeep(grupo.value);
      storeHabitacion.setInformacionAlojamientoFromRGrupoDTO(grupoRaw);
      if (grupoRaw.viaje && grupoRaw.viaje.id) {
        await storeHabitacion.loadInformacionViaje(grupoRaw.viaje.id, grupoRaw.viaje.codigo ?? null);
      }

      grupoRaw.reservas.forEach(reserva => {
        reserva.usuario = {
          nombre: reserva.nombre,
          apellidos: reserva.apellidos,
          apellido: reserva.apellidos,
          fechaNacimiento: reserva.fechaNacimiento,
        };
        storeHabitacion.addReserva(reserva);
      });
    } catch (error) {
      console.error(error);
    } finally {
      initializing.value = false;
      loadingStore.hide();
    }
  }

  onBeforeMount(async () => await init());
</script>

<style scoped>
  .container-card {
    max-width: 1200px;
    margin: auto;
    padding-top: 14rem;
    padding-bottom: 4rem;
  }

  .card {
    border: 0;
    background-color: #f9f7f8;
    border-radius: var(--bs-border-radius-xxl);
  }

  .btn-secondary:hover {
    color: white;
  }

  .main {
    background-size: cover;
    background-repeat: no-repeat;
    margin: 0 35px;
  }

  .row {
    display: flex;
    flex-wrap: wrap;
  }

  .col-md-6 {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  @media (max-width: 1000px) {
    .col-12 {
      width: 100%;
      display: block;
    }

    .text-end {
      text-align: start;
    }

    .container-card {
      padding-top: 6rem;
    }

    .importe,
    .price {
      font-size: 15px !important;
    }
  }

  .outer-card {
    width: 100%;
    background-color: white;
    position: relative;
    border: 1px solid #ccc;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 20px;
    height: 85px;
    border-radius: var(--bs-border-radius-xl);
    box-shadow: 0px 2px 4px 0px #00000080;
  }

  .inner-card {
    width: 30%;
    height: 100%;
    position: absolute;
    right: 0;

    .price {
      background-color: var(--bs-primary);
      border-radius: var(--bs-border-radius-xl);
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      box-shadow: 0px 2px 4px 0px #00000080;
      border-left: 1px solid #ddd;
    }
  }
</style>
